import { useEffect, useState } from 'react';
import { useResizablePanel, type UseResizablePanelReturn } from '@localagent/ui/hooks/use-resizable-panel';

export const MIN_TREE_W = 160;
export const MAX_TREE_W_FLOOR = 480;
export const DEFAULT_TREE_W = 224;

export const maxTreeWidthFor = (windowWidth: number) =>
  Math.max(MAX_TREE_W_FLOOR, Math.floor(windowWidth / 2));

export function useMaxTreeWidth(): number {
  const [max, setMax] = useState(() => maxTreeWidthFor(window.innerWidth));
  useEffect(() => {
    const onResize = () => setMax(maxTreeWidthFor(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return max;
}

/**
 * Shared drag-resize wiring for a docked directory-tree column: window-aware
 * max clamp, min clamp, persisted-width hydration (once, on first arrival —
 * app-shell ADR-c857a), and the pointer-based `useResizablePanel` commit.
 * Each host (chat-docked tree, standalone Files tab tree) supplies its OWN
 * preference field via `persistedWidth`/`onCommitWidth` — the two hosts never
 * read each other's width (app-shell ADR-c857a).
 */
export function useTreeColumnResize({
  persistedWidth,
  onCommitWidth,
}: {
  persistedWidth: number | undefined;
  onCommitWidth: (width: number) => void;
}): UseResizablePanelReturn {
  const maxTreeW = useMaxTreeWidth();
  const clampTreeW = (w: number) => Math.max(MIN_TREE_W, Math.min(maxTreeW, w));
  const [treeWidth, setTreeWidth] = useState(DEFAULT_TREE_W);
  // (app-shell ADR-c857a)
  const [committedRef] = useState({ committed: false });
  useEffect(() => {
    if (!committedRef.committed && typeof persistedWidth === 'number') {
      setTreeWidth(persistedWidth);
    }
  }, [persistedWidth, committedRef]);
  return useResizablePanel({
    axis: 'horizontal',
    handleEdge: 'end',
    size: clampTreeW(treeWidth),
    onCommit: (size) => {
      committedRef.committed = true;
      const clamped = clampTreeW(size);
      setTreeWidth(clamped);
      onCommitWidth(clamped);
    },
    min: MIN_TREE_W,
    max: maxTreeW,
  });
}
